import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import AppUpdateNotice from "../components/AppUpdateNotice";

// Faster polling in development so local testing does not need a 5-minute wait.
const POLL_INTERVAL_MS =
  process.env.NODE_ENV === "development" ? 10 * 1000 : 5 * 60 * 1000;
const TOAST_ID = "app-version-update";
const DEV_BASELINE_KEY = "appVersionBaseline";

const isDev = process.env.NODE_ENV === "development";

function readDevBaseline() {
  if (!isDev) return null;
  try {
    return sessionStorage.getItem(DEV_BASELINE_KEY);
  } catch {
    return null;
  }
}

function writeDevBaseline(version) {
  if (!isDev) return;
  try {
    sessionStorage.setItem(DEV_BASELINE_KEY, version);
  } catch {
    // ignore quota / private mode errors
  }
}

async function fetchAppVersion() {
  const base = process.env.PUBLIC_URL || "";
  const candidates = [
    `${base}/version.json?t=${Date.now()}`,
    `${base}/static/version.json?t=${Date.now()}`,
  ];

  let lastError;

  for (const url of candidates) {
    try {
      const response = await fetch(url, {
        cache: "no-store",
        headers: {
          Pragma: "no-cache",
          "Cache-Control": "no-cache",
          Accept: "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const contentType = (response.headers.get("content-type") || "").toLowerCase();
      const text = await response.text();

      // SPA servers often fall back to index.html when the file is missing.
      if (
        contentType.includes("text/html") ||
        text.trimStart().startsWith("<!doctype") ||
        text.trimStart().startsWith("<html")
      ) {
        throw new Error(`got HTML instead of JSON from ${url}`);
      }

      const data = JSON.parse(text);
      if (!data || data.version == null) {
        throw new Error("missing version field");
      }

      return String(data.version);
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError || new Error("Failed to fetch version.json");
}

/**
 * Polls /version.json and notifies once when a new build is available.
 * Reload happens only when the user clicks the update action.
 *
 * In development, CRA reloads the page when public/version.json changes.
 * We keep the baseline in sessionStorage so the update can still be detected
 * after that automatic reload. Production uses in-memory baseline only.
 */
export default function useAppVersion() {
  const { t, i18n } = useTranslation("global");
  const currentVersionRef = useRef(null);
  const notifiedVersionRef = useRef(null);
  const pendingRemoteRef = useRef(null);
  const tRef = useRef(t);
  const languageRef = useRef(i18n.language);
  const [updateAvailable, setUpdateAvailable] = useState(false);

  tRef.current = t;
  languageRef.current = i18n.language;

  useEffect(() => {
    let cancelled = false;

    const applyUpdate = () => {
      const remoteVersion = pendingRemoteRef.current;
      if (remoteVersion) {
        writeDevBaseline(remoteVersion);
        currentVersionRef.current = remoteVersion;
      }
      window.location.reload();
    };

    const showUpdateToast = (remoteVersion) => {
      if (notifiedVersionRef.current === remoteVersion) {
        return;
      }

      notifiedVersionRef.current = remoteVersion;
      pendingRemoteRef.current = remoteVersion;
      setUpdateAvailable(true);

      const isRtl = languageRef.current === "ar";

      toast.dismiss(TOAST_ID);

      toast(
        ({ closeToast }) => (
          <AppUpdateNotice
            isRtl={isRtl}
            title={tRef.current("appUpdate.title")}
            hint={tRef.current("appUpdate.hint")}
            actionLabel={tRef.current("appUpdate.updateNow")}
            laterLabel={tRef.current("appUpdate.later")}
            onUpdate={() => {
              closeToast();
              applyUpdate();
            }}
            onLater={() => {
              closeToast();
            }}
          />
        ),
        {
          toastId: TOAST_ID,
          className: "app-update-toast",
          autoClose: false,
          closeOnClick: false,
          draggable: false,
          closeButton: false,
          hideProgressBar: true,
          position: "top-right",
          style: {
            background: "transparent",
            boxShadow: "none",
            padding: 0,
            width: "auto",
          },
        }
      );
    };

    const checkVersion = async () => {
      try {
        const remoteVersion = await fetchAppVersion();
        if (cancelled) return;

        if (currentVersionRef.current == null) {
          const storedBaseline = readDevBaseline();
          currentVersionRef.current = storedBaseline || remoteVersion;

          if (!storedBaseline) {
            writeDevBaseline(remoteVersion);
            // eslint-disable-next-line no-console
        
            return;
          }

         
        }

        if (remoteVersion !== currentVersionRef.current) {
          // eslint-disable-next-line no-console
      
          showUpdateToast(remoteVersion);
        } else if (isDev) {
     
        }
      } catch (error) {
    
      }
    };

    window.__checkAppVersion = checkVersion;
    window.__testAppUpdateToast = () => showUpdateToast(`test-${Date.now()}`);
    window.__resetAppVersionBaseline = () => {
      try {
        sessionStorage.removeItem(DEV_BASELINE_KEY);
      } catch {
        // ignore
      }
      currentVersionRef.current = null;
      notifiedVersionRef.current = null;
      // eslint-disable-next-line no-console
      // console.log("[appVersion] baseline reset — call __checkAppVersion()");
    };
    // Used by the banner button in AppVersionChecker.
    window.__applyAppUpdate = applyUpdate;

    checkVersion();
    const intervalId = window.setInterval(checkVersion, POLL_INTERVAL_MS);

    const onVisible = () => {
      if (document.visibilityState === "visible") {
        checkVersion();
      }
    };

    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", checkVersion);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", checkVersion);
      if (window.__checkAppVersion === checkVersion) {
        delete window.__checkAppVersion;
      }
      delete window.__testAppUpdateToast;
      delete window.__resetAppVersionBaseline;
      delete window.__applyAppUpdate;
    };
  }, []);

  return { updateAvailable };
}
